import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import type { FeatureComparisonClient, FeatureComparisonSelectOption } from '../../../core/tokens/feature-comparison-client.token';
import { FAVORITES_CLIENT } from '../../../core/tokens/favorites-client.token';
import { EDashboards } from '../../../shared/enums/EDashboards';
import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';
import type { TableDataResponse } from '../../../core/tokens/feature-comparison-client.token';
import type { ComparisonTableRow } from '../models/comparison-table-row.model';

const NO_FAVORITES_OPTION: FeatureComparisonSelectOption[] = [
  { label: 'No favorites saved', value: '' },
];

@Injectable({
  providedIn: 'root',
})
export class FeatureComparisonService implements FeatureComparisonClient {
  private readonly favoritesClient = inject(FAVORITES_CLIENT);

  readonly featureFilters = signal<FeatureComparisonSelectOption[]>([]);
  readonly comparisonTimePeriodOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly comparisonAuditOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly comparisonMethodOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly comparisonFavoritesOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly filterFavoritesOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly medicationOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly clinicalUnitOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly serviceDeptOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly patientNameOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly patientIdOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly employeeOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly employeeIdOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly employeeJobRoleOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly shiftOptions = signal<FeatureComparisonSelectOption[]>([]);
  readonly fteStatusOptions = signal<FeatureComparisonSelectOption[]>([]);

  constructor() {
    this.loadComparisonFavorites();
    this.loadFilterFavorites();
  }

  /** Load favorites for the comparison filters dashboard (saved comparison settings). */
  private loadComparisonFavorites(): void {
    this.favoritesClient
      .loadFavoritesAsOptionsByDashboard(EDashboards.FEATURE_COMPARISON_FILTERS)
      .pipe(
        tap((options) => {
          this.comparisonFavoritesOptions.set(options);
        }),
        catchError(() => of(NO_FAVORITES_OPTION))
      )
      .subscribe();
  }

  /** Load favorites for the feature filters dashboard (saved feature filter sets). */
  private loadFilterFavorites(): void {
    this.favoritesClient
      .loadFavoritesAsOptionsByDashboard(EDashboards.FEATUE_COMPARISON_FEATURES)
      .pipe(
        tap((options) => {
          this.filterFavoritesOptions.set(options);
        }),
        catchError(() => of(NO_FAVORITES_OPTION))
      )
      .subscribe();
  }

  getComparisonTableRows(_query: TableQuery): Observable<TableDataResponse<ComparisonTableRow>> {
    // Real API endpoint can be wired here when available
    return of({ data: [], totalRecords: 0 });
  }

  getFeatureLabel(featureId: string): string {
    const num = featureId.replace(/^feature/i, '') || '1';
    return `Feature ${num}`;
  }

  getLineChartConfig(
    _featureIds: string[],
    _options?: { dateRange?: { start: Date; end: Date }; metricLabel?: string }
  ): null {
    // Real chart API can be wired here when available
    return null;
  }
}
